import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search, ArrowRight, LayoutDashboard, Users, Target, Brain, FileText, GraduationCap, BookOpen, Zap, Library, Shield, Settings, Play } from "lucide-react";
import { GOLD } from "../constants/theme";
import { CONTENT, notShelved } from "../constants/navigation";

/**
 * CommandPalette — ⌘K quick launcher.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - go: (navId) => void — navigation function from BAMFull
 *  - dark: boolean
 */
export function CommandPalette({ open, onClose, go, dark = true }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Static navigation + action index
  const items = useMemo(() => [
    { id: "dashboard", label: "Home", hint: "Your home base", Icon: LayoutDashboard, kind: "nav" },
    { id: "community", label: "Community", hint: "Coach feed", Icon: Users, kind: "nav" },
    { id: "pd", label: "Player Development · Drills & Games", hint: "Skill drills", Icon: Target, kind: "nav" },
    { id: "insights", label: "Player Development · Insights", hint: "Articles", Icon: Brain, kind: "nav" },
    { id: "team", label: "Team Coaching · Drills & Games", hint: "Team drills", Icon: Target, kind: "nav" },
    { id: "ti", label: "Team Coaching · Insights", hint: "Team articles", Icon: Brain, kind: "nav" },
    { id: "xo", label: "X & O Breakdowns", hint: "Play breakdowns", Icon: FileText, kind: "nav" },
    { id: "plans", label: "Training Plans", hint: "Plan builder", Icon: FileText, kind: "nav" },
    { id: "master", label: "Masterclasses", hint: "Video courses", Icon: GraduationCap, kind: "nav" },
    { id: "workouts", label: "Full Workouts", hint: "Complete workouts", Icon: Zap, kind: "nav" },
    { id: "playbook", label: "Playbook Builder", hint: "Design plays", Icon: BookOpen, kind: "nav" },
    { id: "resources", label: "Resources", hint: "Research, books, products", Icon: Library, kind: "nav" },
    { id: "certified", label: "BAM Certification", hint: "Certification course", Icon: Shield, kind: "nav" },
    { id: "settings", label: "Settings", hint: "Preferences & account", Icon: Settings, kind: "nav" },
  ], []);

  // Content index — flatten all drills/articles for cross-section search
  const contentIndex = useMemo(() => {
    const out = [];
    Object.entries(CONTENT || {}).forEach(([sectionId, section]) => {
      (section.items || []).filter(notShelved).forEach(item => {
        out.push({
          id: `${sectionId}-${item.id}`,
          itemId: item.id,
          sectionId,
          label: item.title,
          hint: `${section.title || sectionId}${item.tag ? ` · ${item.tag}` : ""}${item.duration ? ` · ${item.duration}` : ""}`,
          Icon: Play,
          kind: "content",
        });
      });
    });
    return out;
  }, []);

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.map(it => ({ ...it, _group: "Pages" }));
    const pageResults = items
      .filter(it => it.label.toLowerCase().includes(q) || it.hint.toLowerCase().includes(q))
      .slice(0, 5)
      .map(it => ({ ...it, _group: "Pages" }));
    const contentResults = contentIndex
      .filter(it => it.label.toLowerCase().includes(q) || it.hint.toLowerCase().includes(q))
      .slice(0, 12)
      .map(it => ({ ...it, _group: "Drills & Content" }));
    return [...pageResults, ...contentResults];
  }, [items, contentIndex, query]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Keep active index in range
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(0);
  }, [filtered, activeIdx]);

  // Open the ITEM, not just its section — content results stash the same
  // deep link ContentPage consumes for bookmarks/search (bam_open_drill).
  const selectItem = (it) => {
    if (it.kind === "content" && it.itemId != null) {
      try { sessionStorage.setItem("bam_open_drill", JSON.stringify({ sectionId: it.sectionId, id: it.itemId })); } catch {}
      go(it.sectionId);
    } else {
      go(it.kind === "content" ? it.sectionId : it.id);
    }
  };

  // Keyboard handling
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") {
        e.preventDefault();
        const item = filtered[activeIdx];
        if (item) { selectItem(item); onClose(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, filtered, activeIdx, go]);

  if (!open) return null;

  const bg = dark ? "#1a1a1a" : "#ffffff";
  const border = dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const text = dark ? "#fff" : "#111";
  const textDim = dark ? "#888" : "#666";
  const activeBg = dark ? "rgba(226,221,159,0.08)" : "rgba(226,221,159,0.15)";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9500,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "14vh 16px 16px",
        animation: "cpFadeIn .18s ease",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 580,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 24px 72px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)",
          fontFamily: "'DM Sans', sans-serif",
          animation: "cpSlideIn .2s cubic-bezier(.2,.8,.2,1)",
        }}
      >
        {/* Search input */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${border}` }}>
          <Search size={18} color={textDim} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to page, search drills, insights..."
            aria-label="Search"
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: text,
              fontSize: 15,
              fontFamily: "inherit",
            }}
          />
          <kbd
            style={{
              padding: "3px 8px",
              borderRadius: 5,
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
              color: textDim,
              fontSize: 11,
              fontFamily: "monospace",
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: 8 }}>
          {filtered.length === 0 && (
            <div style={{ padding: "32px 20px", textAlign: "center", color: textDim, fontSize: 13 }}>
              No matches for "{query}"
            </div>
          )}
          {filtered.map((it, i) => {
            const active = i === activeIdx;
            const Icon = it.Icon;
            const prevGroup = i > 0 ? filtered[i-1]._group : null;
            const showHeader = it._group && it._group !== prevGroup;
            return (
              <React.Fragment key={it.id}>
              {showHeader && (
                <div style={{ padding: "8px 12px 4px", fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 1, textTransform: "uppercase" }}>
                  {it._group}
                </div>
              )}
              <div
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => { selectItem(it); onClose(); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: active ? activeBg : "transparent",
                  transition: "background .1s ease",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 7,
                    background: active ? `${GOLD}25` : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={15} color={active ? GOLD : textDim} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.label}
                  </div>
                  <div style={{ fontSize: 11, color: textDim, marginTop: 2 }}>{it.hint}</div>
                </div>
                {active && <ArrowRight size={13} color={GOLD} />}
              </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: "10px 16px",
          borderTop: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          gap: 16,
          fontSize: 11,
          color: textDim,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <kbd style={kbdStyle(dark)}>↑</kbd>
            <kbd style={kbdStyle(dark)}>↓</kbd>
            <span>navigate</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <kbd style={kbdStyle(dark)}>↵</kbd>
            <span>open</span>
          </div>
          <div style={{ flex: 1 }} />
          <div>{filtered.length} results</div>
        </div>
      </div>
    </div>
  );
}

const kbdStyle = (dark) => ({
  padding: "2px 6px",
  borderRadius: 4,
  background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
  fontSize: 10,
  fontFamily: "monospace",
});
